import { useCallback, useRef, useEffect } from 'react'
import isEqual from 'lodash.isequal'
import useGroupData from './useGroupData'

// helps find changes in group data and dividers
const useChanges = ({ dividerOffsets }) => {
  const initialDividerOffsets = useRef()
  const { group, initialGroup } = useGroupData()

  // returns true if the two are differences between the data of the groups, false if there is not
  const getGroupHasBeenEdited = useCallback(() => {
    if (!initialGroup.childGroups) return false
    const groupPreChanges = initialGroup.childGroups.find((childGroup) => childGroup.id === group.id)
    if (!group?.coverages || !groupPreChanges?.imageViewerCoverages) return false
    if (groupPreChanges.imageViewerCoverages?.length !== group.coverages?.length) return true

    // Creators are assigned keys in useGroupData for the group itself, but not the initialGroup, so we need to remove those keys here
    const creatorKeys = Object.keys(group?.metadata?.creator || {})
    const sanitizedCreator = creatorKeys.length > 0 ? creatorKeys.map((key) => group?.metadata?.creator[key]) : []

    const preMetadata = {
      archivalReferenceNumber: groupPreChanges?.metadata?.archivalReferenceNumber,
      title: groupPreChanges?.metadata?.title,
      // volume instead of volumes because volume is the input field value
      volume: groupPreChanges?.metadata?.volume,
      creator: groupPreChanges?.metadata?.creator || [],
      languages: groupPreChanges?.metadata?.languages,
    }

    const groupMetadata = {
      archivalReferenceNumber: group?.metadata?.archivalReferenceNumber,
      title: group?.metadata?.title,
      volume: group?.metadata?.volume,
      creator: sanitizedCreator,
      languages: group?.metadata?.languages,
    }

    if (!isEqual(preMetadata, groupMetadata)) return true

    for (let i = 0; i < group.coverages.length; i++) {
      const one = groupPreChanges.imageViewerCoverages[i]
      const two = group.coverages[i]

      if (one.place?.id !== two.place?.id || one.recordTypes.length !== two.recordTypes.length) return true

      for (let j = 0; j < one.recordTypes.length; j++) {
        const oneDate = one.recordTypes[j]?.date
        const twoDate = two.recordTypes[j]?.date

        if (oneDate && !twoDate) return true
        if (one.recordTypes[j].name !== two.recordTypes[j].name || oneDate?.length !== twoDate?.length) return true

        for (let k = 0; k < oneDate?.length; k++)
          if (oneDate[k]?.fromTimestamp !== twoDate[k]?.fromTimestamp) return true
      }
    }

    return false
  }, [initialGroup, group])

  useEffect(() => {
    if (dividerOffsets && !initialDividerOffsets.current && dividerOffsets.length !== 0) {
      initialDividerOffsets.current = dividerOffsets
    }
  }, [dividerOffsets])

  const getIsDividerOffsetChanges = useCallback(() => {
    if (
      (dividerOffsets?.length < 0 && !initialDividerOffsets.current) ||
      dividerOffsets?.length !== initialDividerOffsets.current?.length
    )
      return true
    return dividerOffsets?.reduce((prev, curr, i) => {
      if (prev) return true
      return curr !== initialDividerOffsets.current[i]
    }, false)
  }, [dividerOffsets])

  return {
    getIsDividerOffsetChanges,
    getGroupHasBeenEdited,
  }
}

export default useChanges
